import React from "react";
import "./HeaderBar.scss";
import {Nullable} from "@data-force/app";
import {
    DownOutlined,
    ExclamationCircleOutlined,
    GlobalOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    Avatar,
    Button,
    Dropdown,
    Menu,
    Modal,
    Select,
    Tooltip,
} from "antd";
import {
    getTranslate,
    Language,
    LocalizeState,
    setActiveLanguage,
    getActiveLanguage,
    Translate,
    TranslateFunction,
    SetActiveLanguageAction,
} from "react-localize-redux";
import {User, UserManager} from "oidc-client";
import {connect} from "react-redux";
import dayjs from "dayjs";
import classNames from "classnames";
import Logo from "../common/Logo/Logo";
import StoreState from "../../interfaces/store/storeState";
import {formatString, getHistoryParams} from "../../utils/common";
import {ViewRoutes} from "../../utils/constants";

interface DispatchProps {
    setActiveLanguage(languageCode: string): SetActiveLanguageAction
}

interface StateProps {
    user: Nullable<User>
    localize: LocalizeState
    translate: TranslateFunction
    onlineHelp: string
    activeLanguage: Language
    profileLink: string
    userManager: Nullable<UserManager>
    isMobile: boolean
}

type Props = StateProps & DispatchProps;

class HeaderBar extends React.Component<Props> {
    private static readonly translationPath: string = "components.HeaderBar.HeaderBar";

    constructor(props: Props) {
        super(props);
        this.getHeaderTitle = this.getHeaderTitle.bind(this);
        this.goToOnlineHelp = this.goToOnlineHelp.bind(this);
        this.getLanguageOptions = this.getLanguageOptions.bind(this);
        this.getLanguageItems = this.getLanguageItems.bind(this);
        this.setLanguage = this.setLanguage.bind(this);
        this.logout = this.logout.bind(this);
        this.confirmLogout = this.confirmLogout.bind(this);
    }

    /**
     * Get dropdown with actions for header component, depending on screen resolution, for mobile screens,
     * we add language picker within the dropdown
     */
    getHeaderDropdown() {
        const {
            isMobile, profileLink, user, translate,
        } : Props = this.props;
        const {translationPath}: typeof HeaderBar = HeaderBar;
        const {SubMenu, Item}: typeof Menu = Menu;
        const userFullName: string = user?.profile.name || "";
        const userEmail: string = user?.profile.email || "";
        const dropdownCls = classNames({
            "df-mobile-header-dropdown": isMobile,
            "df-desktop-header-dropdown": isMobile,
        });

        return (
            <Dropdown
                trigger={["click"]}
                overlayClassName={dropdownCls}
                overlay={(
                    <Menu>
                        <Item key="my_profile">
                            <a href={profileLink} target="_blank" rel="noopener noreferrer" >
                                <Translate id={`${translationPath}.profile.text`}/>
                            </a>
                        </Item>
                        <Item key="user_guide" onClick={this.goToOnlineHelp}>
                            <Translate id={`${translationPath}.help.text`}/>
                        </Item>
                        {isMobile && (
                            <SubMenu
                                title={<Translate id={`${translationPath}.language.text`}/>}
                                className="df-submenu-language"
                                key="available_language"
                            >
                                {this.getLanguageItems()}
                            </SubMenu>
                        )}
                        <Item key="logout" onClick={this.confirmLogout}>
                            <Translate id={`${translationPath}.signOut.text`}/>
                        </Item>
                    </Menu>
                )}
            >
                <Button type="link" className="df-header-profile-btn">
                    <Avatar size={32} icon={<UserOutlined aria-hidden/>}/>
                    <div>
                        {userFullName}
                        <div className="df-header-email">{userEmail}</div>
                    </div>
                    <DownOutlined aria-label={translate(`${translationPath}.dropdownMenu.text`) as string}/>
                </Button>
            </Dropdown>
        );
    }

    /**
     * Get header title based on current view's active route, if root route provide welcome msg
     * @param {string} name
     * @param {Nullable<string>} view
     * @return {React.ReactNode}
     */
    getHeaderTitle(name: string, view: Nullable<string>): React.ReactNode {
        const {translate} : Props = this.props;
        const welcomeFormat: string = translate(`${HeaderBar.translationPath}.welcomeFormat.text`) as string;

        return (
            <h1 className="df-header-title">
                {view ? <Translate id={`${HeaderBar.translationPath}.${view}.text`}/> : (
                    <Tooltip
                        title={formatString(welcomeFormat, name)}
                        placement="topLeft"
                        overlayClassName="df-header-title-tooltip"
                    >
                        {formatString(welcomeFormat, name)}
                    </Tooltip>
                )}
            </h1>
        );
    }

    /**
     * Displays a list of language for select menu,  with the ability to set another on click or enter, the active one is disabled
     * @return {React.ReactNode[]}
     */
    getLanguageOptions(): React.ReactNode[] {
        const {localize: {languages}}: Props = this.props;
        return (
            languages?.map(({code, active, name}: Language, index: number): React.ReactNode => (
                <Select.Option
                    key={code + index}
                    disabled={active}
                    value={code}
                >
                    {name}
                </Select.Option>
            )
            )
        );
    }

    /**
     * Displays a list of language for dropdown menu on mobile view with the ability to set another on click, the active one is disabled
     * @return {React.ReactNode[]}
     */
    getLanguageItems(): React.ReactNode[] {
        const {localize: {languages}}: Props = this.props;
        return (
            languages?.map(({code, active, name}: Language, index: number): React.ReactNode => (
                <Menu.Item
                    key={code + index}
                    disabled={active}
                    onClick={this.setLanguage.bind(this, code)}
                >
                    {name}
                </Menu.Item>
            )
            )
        );
    }

    /**
     * Based on currently selected language code, we update app localization and day.js localization
     * @param {string} code
     */
    setLanguage(code:string): void {
        this.props.setActiveLanguage(code);
        dayjs.locale(code);
    }

    /**
     * Redirect to online help in new window
     */
    goToOnlineHelp(): void {
        window.open(this.props.onlineHelp);
    }

    /**
     * Open confirm modal
     */
    confirmLogout(): void {
        const {translationPath}: typeof HeaderBar = HeaderBar;
        const {confirm}: typeof Modal = Modal;
        const {translate}: Props = this.props;
        confirm({
            className: "df-warning-confirm",
            title: translate(`${translationPath}.signOut.text`),
            content: translate(`${translationPath}.content.text`),
            okText: translate(`${translationPath}.signOut.text`),
            cancelText: translate(`${translationPath}.cancel.text`),
            icon: <ExclamationCircleOutlined aria-hidden/>,
            centered: true,
            onOk: this.logout,
        });
    }

    /**
     * Logout user
     */
    logout(): void {
        this.props.userManager!.signoutRedirect().catch();
    }

    render(): React.ReactNode {
        const {
            user, activeLanguage, translate,
        }: Props = this.props;
        const {translationPath}: typeof HeaderBar = HeaderBar;
        const view: Nullable<string> = getHistoryParams()?.view;

        const userFirstName: string = user?.profile.given_name || "";
        return (
            <header className="df-header">
                <div>
                    <Logo dark/>
                    {(!view || ViewRoutes.includes(view)) && this.getHeaderTitle(userFirstName, view)}
                </div>
                <div className="df-header-actions">
                    <span className="df-select-language">
                        <GlobalOutlined aria-hidden/>
                        <Select
                            value={activeLanguage.name}
                            onChange={this.setLanguage}
                            aria-label={translate(`${translationPath}.dropdownMenu.text`) as string}
                        >
                            {this.getLanguageOptions()}
                        </Select>
                    </span>
                    <span className="df-header-divider"/>
                    {this.getHeaderDropdown()}
                </div>
            </header>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        user: state.oidc.user,
        localize: state.localize,
        translate: getTranslate(state.localize),
        onlineHelp: state.serviceProperties.onlineHelp,
        activeLanguage: getActiveLanguage(state.localize),
        profileLink: state.serviceProperties.profileLink,
        userManager: state.userManager,
        isMobile: state.mobile.isMobile,
    };
}

const mapDispatchToProps: DispatchProps = {
    setActiveLanguage,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderBar);
